import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import {
  isOpenBrowsePublicPath,
  isPublicApiUrl,
} from '@/constants/platformBrowseMode';
import { isDefinitiveAuthFailure } from '@/lib/authFailure';
import { refreshAccessToken, REFRESH_TIMEOUT_MS } from '@/lib/refreshAccessToken';
import { debug } from '@/utils/logger';
import { isTokenExpired } from '@/utils/tokenUtils';

function normalizePathname(pathname: string): string {
    if (!pathname || pathname === '/') return pathname || '/';
    return pathname.replace(/\/$/, '');
}

// Track if we're currently refreshing to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
// Queue of requests waiting for token refresh
let refreshQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
}> = [];

/**
 * Process queued requests after token refresh completes
 */
const processQueue = (error: Error | null, token: string | null = null) => {
    refreshQueue.forEach((promise) => {
        if (error) {
            promise.reject(error);
        } else if (token) {
            promise.resolve(token);
        }
    });
    refreshQueue = [];
};

/**
 * Clear auth state and redirect to home
 */
const clearAuthAndRedirect = () => {
    debug('🔐 [axiosInterceptor] Clearing auth state and redirecting to home');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('hasOnboarded');
    if (typeof window !== 'undefined') {
        window.location.href = '/';
    }
};

export const setupAxiosAuthInterceptor = () => {
    // Safety net so no request (with or without a per-call timeout) can hang forever
    // and leave a caller's isLoading flag stuck true.
    if (!axios.defaults.timeout) {
        axios.defaults.timeout = 30000;
    }

    // Request interceptor
    axios.interceptors.request.use(
        async (config: InternalAxiosRequestConfig) => {
            // Check if this is a public endpoint that shouldn't have auth headers
            const url = config.url || '';
            const baseURL = config.baseURL || '';
            const fullUrl = baseURL ? `${baseURL}${url}` : url;

            const isPublicEndpoint = isPublicApiUrl(fullUrl);

            // Only add auth headers if it's not a public endpoint
            if (!isPublicEndpoint) {
                let token = localStorage.getItem('accessToken');
                let idToken = localStorage.getItem('idToken');

                // Check if tokens are expired
                const accessTokenExpired = token && isTokenExpired(token);
                const idTokenExpired = idToken && isTokenExpired(idToken);

                if (accessTokenExpired || idTokenExpired) {
                    debug('🔐 [axiosInterceptor] Token expired, attempting refresh', {
                        url: fullUrl,
                        accessTokenExpired,
                        idTokenExpired
                    });

                    // If already refreshing, wait for that to complete
                    if (isRefreshing) {
                        debug('🔐 [axiosInterceptor] Refresh already in progress, queuing request');
                        try {
                            token = await new Promise<string>((resolve, reject) => {
                                refreshQueue.push({ resolve, reject });
                            });
                            idToken = localStorage.getItem('idToken');
                        } catch (error) {
                            return Promise.reject(error);
                        }
                    } else {
                        // Start refresh process
                        isRefreshing = true;

                        try {
                            const refreshResult = await refreshAccessToken('axiosInterceptor');

                            if (refreshResult.ok) {
                                token = refreshResult.accessToken;
                                idToken = localStorage.getItem('idToken');
                                processQueue(null, refreshResult.accessToken);
                            } else {
                                const error = new Error('Token refresh failed');
                                processQueue(error);
                                if (refreshResult.shouldLogout) {
                                    clearAuthAndRedirect();
                                }
                                return Promise.reject(error);
                            }
                        } finally {
                            isRefreshing = false;
                        }
                    }
                }

                if (token) {
                    config.headers.Authorization = `${token}`;
                }

                if (idToken) {
                    config.headers['x-id-token'] = `${idToken}`;
                }
            } else {
                // Explicitly remove auth headers for public endpoints
                delete config.headers.Authorization;
                delete config.headers['x-id-token'];
            }

            return config;
        },
        (error) => Promise.reject(error)
    );

    // Response interceptor
    axios.interceptors.response.use(
        (response) => {
            return response;
        },
        async (error: AxiosError) => {
            const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
            const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
            const normalizedPath = normalizePathname(currentPath);
            const publicRoutes = ['/', '/auth/callback', '/faq', '/redirect'];
            // /list/[id], /place/[id], and /itinerary/[id] are public share-link destinations (access control handled in route components)
            const isPublicRoute =
                publicRoutes.includes(normalizedPath)
                || normalizedPath.startsWith('/post/')
                || normalizedPath.startsWith('/profile/')
                || normalizedPath.startsWith('/storefront/')
                || normalizedPath.startsWith('/book/')
                || normalizedPath.startsWith('/list/')
                || normalizedPath.startsWith('/place/')
                || normalizedPath.startsWith('/itinerary/')
                || isOpenBrowsePublicPath(normalizedPath);

            const skipAuthErrorHandling = (originalRequest as InternalAxiosRequestConfig & { skipAuthErrorHandling?: boolean })?.skipAuthErrorHandling === true;
            const isShareTokenEndpoint = originalRequest?.url?.includes('/api/v2/share/tokens');
            const isAuthEndpoint = originalRequest?.url?.includes('/user/authenticated');
            const isPublicApiRequest = isPublicApiUrl(originalRequest?.url);

            // Handle 401/403 responses
            if (error.response?.status === 401 || error.response?.status === 403) {
                // Special handling for auth endpoint failures - always logout
                // EXCEPT: Don't redirect for banned/deleted accounts - let login handler show toast first
                if (isAuthEndpoint) {
                    const errorBody = error.response?.data as { error?: string } | undefined;
                    const isBannedOrDeleted = errorBody?.error === 'Account banned' || errorBody?.error === 'Account deleted';
                    
                    if (isBannedOrDeleted) {
                        // Let banned/deleted errors propagate to login handler so it can show toast
                        debug('🔐 [axiosInterceptor] Banned/deleted account detected - allowing error to propagate', {
                            status: error.response?.status,
                            error: errorBody?.error,
                            url: originalRequest?.url
                        });
                        return Promise.reject(error);
                    }

                    const isDefinitiveAuthError = isDefinitiveAuthFailure(
                        error.response?.status,
                        error.response?.data
                    );

                    if (isDefinitiveAuthError) {
                        debug('🔐 [axiosInterceptor] Auth endpoint definitive auth failure - forcing logout', {
                            status: error.response?.status,
                            url: originalRequest?.url
                        });
                        clearAuthAndRedirect();
                        return Promise.reject(error);
                    }

                    // Do not force logout on ambiguous auth-endpoint failures (e.g. transient upstream issues).
                    debug('🔐 [axiosInterceptor] Auth endpoint failure appears transient/ambiguous - keeping session', {
                        status: error.response?.status,
                        url: originalRequest?.url
                    });
                    return Promise.reject(error);
                }

                // Skip handling for share token endpoints, public browse routes, or public API reads
                if (skipAuthErrorHandling || isShareTokenEndpoint || isPublicRoute || isPublicApiRequest) {
                    return Promise.reject(error);
                }

                // Already retried once with a freshly refreshed token and the
                // endpoint still refuses. If the token were the problem the retry
                // would have fixed it, so only tear down the session when the
                // response actually says this is an auth failure. A permission or
                // business-rule 403 must stay the caller's problem: destroying the
                // session here wipes localStorage and hard-navigates to '/', which
                // is what logs users out mid-page (e.g. the skeletons on
                // /my-travel/lists followed by a reload).
                if (originalRequest._retry) {
                    if (isDefinitiveAuthFailure(error.response?.status, error.response?.data)) {
                        debug('🔐 [axiosInterceptor] Retry still failing with a definitive auth error, logging out');
                        clearAuthAndRedirect();
                    } else {
                        debug('🔐 [axiosInterceptor] Retry still failing but not an auth error - keeping session', {
                            status: error.response?.status,
                            url: originalRequest?.url
                        });
                    }
                    return Promise.reject(error);
                }

                // Try to refresh and retry the request
                originalRequest._retry = true;

                if (isRefreshing) {
                    // Wait for ongoing refresh
                    try {
                        const token = await new Promise<string>((resolve, reject) => {
                            refreshQueue.push({ resolve, reject });
                        });
                        originalRequest.headers.Authorization = token;
                        originalRequest.headers['x-id-token'] = localStorage.getItem('idToken') || '';
                        return axios(originalRequest);
                    } catch (refreshError) {
                        return Promise.reject(refreshError);
                    }
                }

                isRefreshing = true;

                try {
                    const refreshResult = await refreshAccessToken('axiosInterceptor');

                    if (refreshResult.ok) {
                        processQueue(null, refreshResult.accessToken);
                        originalRequest.headers.Authorization = refreshResult.accessToken;
                        originalRequest.headers['x-id-token'] = localStorage.getItem('idToken') || '';
                        return axios(originalRequest);
                    } else {
                        const refreshError = new Error('Token refresh failed');
                        processQueue(refreshError);
                        if (refreshResult.shouldLogout) {
                            clearAuthAndRedirect();
                        }
                        return Promise.reject(refreshError);
                    }
                } finally {
                    isRefreshing = false;
                }
            }

            return Promise.reject(error);
        }
    );
};

// Re-export for tests that reference the timeout constant
export { REFRESH_TIMEOUT_MS };
